import { useCallback, useRef, useState } from 'react'

/**
 * Pointer-drag offset for floating panels (token tooltip, character sheet).
 * Returns an accumulated {x,y} offset and a pointer-down handler to put on the
 * panel's drag handle (its header). The consumer decides how to apply the
 * offset — absolute left/top, or a translate() on a centred element.
 *
 * Drags that start on an interactive control (button/input/select/textarea/a)
 * are ignored so the header's own buttons still work. Works for mouse and
 * touch (pointer events).
 */
export function useDragOffset() {
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const base = useRef({ x: 0, y: 0 })
  const start = useRef({ x: 0, y: 0 })

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('button, input, select, textarea, a, label')) return
      e.preventDefault()
      base.current = offset
      start.current = { x: e.clientX, y: e.clientY }
      setDragging(true)

      const move = (ev: PointerEvent) => {
        setOffset({
          x: base.current.x + (ev.clientX - start.current.x),
          y: base.current.y + (ev.clientY - start.current.y),
        })
      }
      const up = () => {
        setDragging(false)
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    [offset],
  )

  return { offset, dragging, onPointerDown }
}
